import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Search,
  Folder,
  LayoutGrid,
  GanttChart,
  ListTodo,
  X,
  Hash,
  Zap,
  Sparkles,
  CircleDashed,
  ShieldAlert,
  Command,
  Filter,
  Calendar,
  Loader2,
  History,
} from 'lucide-react';
import type { Task, Space, ViewMode } from '@/types';
import { getInitials, getAvatarColor, formatDate } from '@/utils/formatters';
import { useProfilesContext } from '@/context/ProfilesContext';
import clsx from 'clsx';
import { useCommandPalette } from '@/hooks/useCommandPalette';
import { normalizeStatus, isCompletedStatus, TASK_STATUS } from '@/constants/taskStatuses';
import { searchTasks } from '@/db/optimizedQueries';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabaseClient';

// ─── localStorage helpers for recent searches & tasks ──────────────────────
const RECENT_SEARCHES_KEY = 'zenda-recent-searches';
const RECENT_TASKS_KEY = 'zenda-recent-tasks';
const MAX_RECENT = 10;

function getRecentSearches(): string[] {
  try {
    return JSON.parse(localStorage.getItem(RECENT_SEARCHES_KEY) || '[]');
  } catch { return []; }
}

function saveRecentSearch(query: string) {
  const trimmed = query.trim();
  if (!trimmed || trimmed.length < 2) return;
  const searches = getRecentSearches().filter(s => s !== trimmed);
  searches.unshift(trimmed);
  localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(searches.slice(0, MAX_RECENT)));
}

function getRecentTaskIds(): string[] {
  try {
    return JSON.parse(localStorage.getItem(RECENT_TASKS_KEY) || '[]');
  } catch { return []; }
}

function saveRecentTaskId(taskId: string) {
  const ids = getRecentTaskIds().filter(id => id !== taskId);
  ids.unshift(taskId);
  localStorage.setItem(RECENT_TASKS_KEY, JSON.stringify(ids.slice(0, MAX_RECENT)));
}

interface CommandItem {
  id: string;
  type: 'task' | 'space' | 'folder' | 'list' | 'action' | 'view' | 'filter';
  title: string;
  subtitle?: string;
  icon: React.ReactNode;
  data?: unknown;
  shortcut?: string[];
}

export function CommandPalette() {
  const { isOpen, onClose, tasks, spaces, savedFilters, onSelectTask, onNavigate, onViewChange, onApplyFilter } =
    useCommandPalette();
  const { resolveDisplayName } = useProfilesContext();
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [serverTaskResults, setServerTaskResults] = useState<CommandItem[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { user } = useAuth();

  // Recent searches from localStorage
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  // Recent tasks (fetched from DB by IDs in localStorage)
  const [recentTaskItems, setRecentTaskItems] = useState<CommandItem[]>([]);
  // Suggested tasks (assigned to user, due soon)
  const [suggestedTaskItems, setSuggestedTaskItems] = useState<CommandItem[]>([]);

  // Load recents & suggestions when palette opens
  useEffect(() => {
    if (!isOpen) return;

    // Recent searches
    setRecentSearches(getRecentSearches());

    // Recent tasks
    const recentIds = getRecentTaskIds();
    if (recentIds.length > 0) {
      supabase
        .from('tasks')
        .select('id, name, status, due_date, assignees')
        .in('id', recentIds.slice(0, 5))
        .then(({ data }) => {
          if (!data) return;
          // Sort by the order in localStorage
          const sorted = recentIds
            .map(id => data.find(t => t.id === id))
            .filter(Boolean) as typeof data;
          setRecentTaskItems(
            sorted.map(t => ({
              id: `recent-${t.id}`,
              type: 'task' as const,
              title: t.name,
              subtitle: t.due_date ? formatDate(t.due_date) : undefined,
              icon: getTaskIcon(t.status),
              data: { id: t.id, name: t.name, status: t.status, dueDate: t.due_date, assignees: t.assignees },
            }))
          );
        });
    } else {
      setRecentTaskItems([]);
    }

    // Suggested tasks (assigned to me, due in next 7 days or overdue, not completed)
    if (user?.id) {
      const now = Date.now();
      const weekFromNow = now + 7 * 86400000;
      supabase
        .from('tasks')
        .select('id, name, status, due_date, assignees, priority')
        .not('status', 'in', '("completed","cancelled")')
        .filter('assignees', 'cs', `{${user.id}}`)
        .lte('due_date', weekFromNow)
        .order('due_date', { ascending: true })
        .limit(5)
        .then(({ data }) => {
          if (!data) return;
          setSuggestedTaskItems(
            data.map(t => ({
              id: `suggested-${t.id}`,
              type: 'task' as const,
              title: t.name,
              subtitle: t.due_date ? formatDate(t.due_date) : undefined,
              icon: t.due_date && t.due_date < now
                ? <ShieldAlert className="w-4 h-4 text-accent-rose" />
                : getTaskIcon(t.status),
              data: { id: t.id, name: t.name, status: t.status, dueDate: t.due_date, assignees: t.assignees },
            }))
          );
        });
    }
  }, [isOpen, user?.id]);

  // Static command items
  const commandItems = useMemo<CommandItem[]>(
    () => [
      {
        id: 'view-list',
        type: 'view',
        title: 'Vista Lista',
        subtitle: 'Visualizza i task come lista compatta',
        icon: <ListTodo className="w-4 h-4" />,
        data: 'list',
        shortcut: ['⌘', '1'],
      },
      {
        id: 'view-board',
        type: 'view',
        title: 'Vista Board',
        subtitle: 'Visualizza i task come Kanban board',
        icon: <LayoutGrid className="w-4 h-4" />,
        data: 'board',
        shortcut: ['⌘', '2'],
      },
      {
        id: 'view-gantt',
        type: 'view',
        title: 'Vista Gantt',
        subtitle: 'Visualizza la timeline dei task',
        icon: <GanttChart className="w-4 h-4" />,
        data: 'gantt',
        shortcut: ['⌘', '3'],
      },
      {
        id: 'view-sprint',
        type: 'view',
        title: 'Vista Sprint',
        subtitle: 'Gestisci sprint e iterazioni',
        icon: <Zap className="w-4 h-4" />,
        data: 'sprint',
        shortcut: ['⌘', '7'],
      },
      {
        id: 'view-calendar',
        type: 'view',
        title: 'Vista Calendario',
        subtitle: 'Visualizza i task nel calendario',
        icon: <Calendar className="w-4 h-4" />,
        data: 'calendar',
        shortcut: ['⌘', '8'],
      },
      {
        id: 'action-all',
        type: 'action',
        title: 'Tutti i Task',
        subtitle: `${tasks.length.toLocaleString()} task nel workspace`,
        icon: <Sparkles className="w-4 h-4" />,
        shortcut: ['⌘', 'A'],
      },
    ],
    [tasks.length]
  );

  // Navigation items (spaces, folders, lists)
  const navigationItems = useMemo<CommandItem[]>(() => {
    const items: CommandItem[] = [];

    spaces.forEach((space: Space) => {
      items.push({
        id: `space-${space.name}`,
        type: 'space',
        title: space.name,
        subtitle: `${space.taskCount.toLocaleString()} task`,
        icon: <Zap className="w-4 h-4 text-accent-amber" />,
        data: { path: space.name },
      });

      space.folders.forEach(
        (folder: { name: string; taskCount: number; lists: Array<{ name: string }> }) => {
          const folderPath = `${space.name}/${folder.name}`;
          items.push({
            id: `folder-${space.name}-${folder.name}`,
            type: 'folder',
            title: folder.name,
            subtitle: `${space.name} • ${folder.taskCount} task`,
            icon: <Folder className="w-4 h-4 text-accent-blue" />,
            data: { path: folderPath },
          });

          folder.lists.forEach((list: { name: string }) => {
            const listPath = `${space.name}/${folder.name}/${list.name}`;
            items.push({
              id: `list-${space.name}-${folder.name}-${list.name}`,
              type: 'list',
              title: list.name,
              subtitle: `${space.name} / ${folder.name}`,
              icon: <Hash className="w-4 h-4 text-accent-cyan" />,
              data: { path: listPath },
            });
          });
        }
      );
    });

    return items;
  }, [spaces]);

  // Saved filter items
  const filterItems = useMemo<CommandItem[]>(() => {
    return savedFilters.map((filter) => ({
      id: `filter-${filter.id}`,
      type: 'filter' as const,
      title: filter.name,
      subtitle: filter.description || 'Filtro salvato',
      icon: <Filter className="w-4 h-4 text-accent-violet" />,
      data: filter,
    }));
  }, [savedFilters]);

  // Debounced server-side task search via PostgreSQL full-text search
  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);

    if (query.length < 2) {
      setServerTaskResults([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    searchTimerRef.current = setTimeout(async () => {
      try {
        const results = await searchTasks({ query, limit: 20 });
        setServerTaskResults(
          results.map((task) => ({
            id: `task-${task.id}`,
            type: 'task' as const,
            title: task.name,
            subtitle: formatDate(task.dueDate),
            icon: getTaskIcon(task.status),
            data: task,
          }))
        );
      } catch {
        setServerTaskResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 200);

    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, [query]);

  // Combined filtered results grouped by type
  const resultGroups = useMemo(() => {
    const groups: { label: string; items: CommandItem[] }[] = [];

    if (!query.trim()) {
      // Recent searches as clickable items
      if (recentSearches.length > 0) {
        groups.push({
          label: 'Ricerche recenti',
          items: recentSearches.slice(0, 5).map((s, i) => ({
            id: `search-${i}`,
            type: 'action' as const,
            title: s,
            icon: <History className="w-4 h-4" />,
            data: { recentSearch: s },
            shortcut: ['↵'],
          })),
        });
      }
      if (recentTaskItems.length > 0) groups.push({ label: 'Recenti', items: recentTaskItems });
      if (suggestedTaskItems.length > 0) groups.push({ label: 'Suggeriti', items: suggestedTaskItems });
      groups.push({ label: 'Comandi rapidi', items: commandItems });
      if (filterItems.length > 0) groups.push({ label: 'Filtri salvati', items: filterItems });
      if (navigationItems.length > 0) groups.push({ label: 'Navigazione', items: navigationItems.slice(0, 5) });
      return groups;
    }

    const lowerQuery = query.toLowerCase();
    const match = (item: CommandItem) =>
      item.title.toLowerCase().includes(lowerQuery) ||
      item.subtitle?.toLowerCase().includes(lowerQuery);

    const filteredCommands = commandItems.filter(match);
    const filteredFilters = filterItems.filter(match);
    const filteredNav = navigationItems.filter(match).slice(0, 8);

    if (filteredCommands.length > 0) groups.push({ label: 'Comandi', items: filteredCommands });
    if (filteredFilters.length > 0) groups.push({ label: 'Filtri salvati', items: filteredFilters });
    if (filteredNav.length > 0) groups.push({ label: 'Navigazione', items: filteredNav });
    if (serverTaskResults.length > 0) groups.push({ label: 'Task', items: serverTaskResults });

    return groups;
  }, [query, commandItems, filterItems, navigationItems, serverTaskResults, recentSearches, recentTaskItems, suggestedTaskItems]);

  // Flat list for keyboard navigation
  const filteredItems = useMemo(
    () => resultGroups.flatMap(g => g.items),
    [resultGroups]
  );

  // Reset selection when results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [filteredItems]);

  // Focus input when opening
  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
      setQuery('');
      setSelectedIndex(0);
    }
  }, [isOpen]);

  const handleSelect = useCallback(
    (item: CommandItem) => {
      // Save recent search if there's a query
      if (query.trim().length >= 2) {
        saveRecentSearch(query.trim());
      }

      switch (item.type) {
        case 'task': {
          const taskData = item.data as Task;
          saveRecentTaskId(taskData.id);
          onSelectTask(taskData);
          break;
        }
        case 'view':
          onViewChange(item.data as ViewMode);
          break;
        case 'action': {
          // Check if this is a recent search click
          const actionData = item.data as { recentSearch?: string } | undefined;
          if (actionData?.recentSearch) {
            setQuery(actionData.recentSearch);
            return; // Don't close - let the search run
          }
          onNavigate(null);
          break;
        }
        case 'space': {
          const spaceData = item.data as { path: string };
          onNavigate(spaceData.path);
          break;
        }
        case 'folder': {
          const folderData = item.data as { path: string };
          onNavigate(folderData.path);
          break;
        }
        case 'list': {
          const listData = item.data as { path: string };
          onNavigate(listData.path);
          break;
        }
        case 'filter': {
          const filterData = item.data as import('@/db/savedFilters').SavedFilter;
          onApplyFilter(filterData);
          break;
        }
      }
      onClose();
    },
    [query, onSelectTask, onViewChange, onNavigate, onApplyFilter, onClose]
  );

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) => (prev < filteredItems.length - 1 ? prev + 1 : prev));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev));
          break;
        case 'Enter':
          e.preventDefault();
          if (filteredItems[selectedIndex]) {
            handleSelect(filteredItems[selectedIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, filteredItems, selectedIndex, onClose, handleSelect]);

  // Scroll selected item into view
  useEffect(() => {
    if (listRef.current) {
      const selectedEl = listRef.current.querySelector(`[data-index="${selectedIndex}"]`);
      selectedEl?.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[12vh]">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-surface-primary/80 backdrop-blur-sm animate-fade-in"
        onClick={onClose}
      />

      {/* Dialog */}
      <div className="relative w-full max-w-xl bg-surface-secondary border border-border-secondary rounded-2xl shadow-2xl overflow-hidden animate-scale-in">
        {/* Search Input */}
        <div className="flex items-center gap-3 px-4 py-4 border-b border-border-primary">
          <div className="w-8 h-8 rounded-lg bg-accent-violet/10 flex items-center justify-center flex-shrink-0">
            <Command className="w-4 h-4 text-accent-violet" />
          </div>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Cerca task, progetti, comandi..."
            className="flex-1 bg-transparent text-[14px] text-text-primary placeholder:text-text-quaternary focus:outline-none"
          />
          {query && (
            <button
              onClick={() => setQuery('')}
              className="p-1.5 rounded-md hover:bg-surface-hover transition-colors"
            >
              <X className="w-4 h-4 text-text-quaternary" />
            </button>
          )}
        </div>

        {/* Results */}
        <div ref={listRef} className="max-h-[50vh] overflow-y-auto">
          {filteredItems.length === 0 ? (
            <div className="py-12 text-center">
              <div className="w-12 h-12 rounded-2xl bg-surface-tertiary mx-auto mb-3 flex items-center justify-center">
                {isSearching ? (
                  <Loader2 className="w-5 h-5 text-text-quaternary animate-spin" />
                ) : (
                  <Search className="w-5 h-5 text-text-quaternary" />
                )}
              </div>
              <p className="text-[13px] text-text-secondary font-medium">
                {isSearching ? 'Ricerca in corso...' : 'Nessun risultato'}
              </p>
              {!isSearching && (
                <p className="text-[11px] text-text-quaternary mt-1">Prova con termini diversi</p>
              )}
            </div>
          ) : (
            <div className="p-2">
              {resultGroups.map((group) => {
                // Calculate the flat index offset for this group
                let groupOffset = 0;
                for (const g of resultGroups) {
                  if (g === group) break;
                  groupOffset += g.items.length;
                }

                return (
                  <div key={group.label} className="mb-1">
                    <div className="px-3 py-1.5">
                      <span className="text-[10px] uppercase tracking-wider text-text-quaternary font-semibold">
                        {group.label}
                      </span>
                    </div>
                    <div className="space-y-0.5">
                      {group.items.map((item, i) => {
                        const flatIndex = groupOffset + i;
                        return (
                          <button
                            key={item.id}
                            data-index={flatIndex}
                            onClick={() => handleSelect(item)}
                            onMouseEnter={() => setSelectedIndex(flatIndex)}
                            className={clsx(
                              'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-left group',
                              flatIndex === selectedIndex ? 'bg-accent-violet-dim' : 'hover:bg-surface-hover'
                            )}
                          >
                            <div
                              className={clsx(
                                'w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors',
                                flatIndex === selectedIndex
                                  ? 'bg-accent-violet/20 text-accent-violet'
                                  : 'bg-surface-tertiary text-text-tertiary'
                              )}
                            >
                              {item.icon}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-[13px] font-medium truncate text-text-primary">
                                {item.title}
                              </div>
                              {item.subtitle && (
                                <div className="text-[11px] text-text-quaternary truncate">
                                  {item.subtitle}
                                </div>
                              )}
                            </div>
                            {item.type === 'task' && ((item.data as Task)?.assignees || []).length > 0 && (() => {
                              const dn = resolveDisplayName(((item.data as Task).assignees || [])[0]);
                              return (
                                <div
                                  className={clsx(
                                    'w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0',
                                    getAvatarColor(dn)
                                  )}
                                >
                                  {getInitials(dn)}
                                </div>
                              );
                            })()}
                            {item.shortcut ? (
                              <div className="flex items-center gap-0.5 flex-shrink-0">
                                {item.shortcut.map((key, ki) => (
                                  <kbd key={ki} className="kbd">
                                    {key}
                                  </kbd>
                                ))}
                              </div>
                            ) : (
                              <span
                                className={clsx(
                                  'text-[10px] px-2 py-0.5 rounded-full capitalize flex-shrink-0 font-medium',
                                  flatIndex === selectedIndex
                                    ? 'bg-accent-violet/20 text-accent-violet'
                                    : 'bg-surface-tertiary text-text-quaternary'
                                )}
                              >
                                {getTypeLabel(item.type)}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center gap-6 px-4 py-2.5 border-t border-border-primary bg-surface-tertiary/30">
          <div className="flex items-center gap-1.5 text-[10px] text-text-quaternary">
            <kbd className="kbd">↑</kbd>
            <kbd className="kbd">↓</kbd>
            <span className="font-medium">naviga</span>
          </div>
          <div className="flex items-center gap-1.5 text-[10px] text-text-quaternary">
            <kbd className="kbd">↵</kbd>
            <span className="font-medium">seleziona</span>
          </div>
          <div className="flex items-center gap-1.5 text-[10px] text-text-quaternary">
            <kbd className="kbd">esc</kbd>
            <span className="font-medium">chiudi</span>
          </div>
          <div className="flex-1" />
          <span className="text-[10px] text-text-quaternary font-medium tabular-nums">
            {filteredItems.length} risultati
          </span>
        </div>
      </div>
    </div>
  );
}

function getTaskIcon(status: string) {
  const s = normalizeStatus(status);
  if (isCompletedStatus(s)) {
    return <Sparkles className="w-4 h-4 text-accent-emerald" />;
  }
  if (s === TASK_STATUS.IN_PROGRESS) {
    return <Zap className="w-4 h-4 text-accent-blue" />;
  }
  if (s === TASK_STATUS.BLOCKED) {
    return <ShieldAlert className="w-4 h-4 text-accent-rose" />;
  }
  return <CircleDashed className="w-4 h-4 text-text-quaternary" />;
}

function getTypeLabel(type: string): string {
  const labels: Record<string, string> = {
    task: 'Task',
    space: 'Space',
    folder: 'Cartella',
    list: 'Lista',
    action: 'Azione',
    view: 'Vista',
    filter: 'Filtro',
  };
  return labels[type] || type;
}
